import FileUploadInput from "Common/Components/FileUploadInput/FileUploadInput";
import FormBreadcrumb from "Common/Components/FormBreadcrumb/FormBreadcrumb";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import {
  fetchWarehousePackingById,
  updateWarehousePacking,
} from "slices/Warehouse/Packing/PackingDone/thunk";
import { createSelector } from "reselect";
import { format, parse } from "date-fns";
import { WarehouseAxios } from "api/WarehouseAxios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { showError, showSuccess, showWarning } from "helpers/swalHelper";
import { Autocomplete, Box } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import LoadingSpinner from "components/LoadingSpinner";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import AddTags from "../components/AddTagsForPacking";
import TextEditor from "components/TextEditor/TextEditor";
import EditIcon from "@mui/icons-material/Edit";

interface PackingFormValues {
  packing_date: string;
  initiated_by: string;
  so_number: string;
  notes: string;
  documents: File | null;
  attachments?: string[]; // Store existing attachment URLs
  tags: string[];
}

interface SerialNumberOption {
  inventory_product_id: string;
  serial_no: string;
}

const EditPacking: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [items, setItems] = useState<any>([]);
  const { id } = useParams();
  const [packingData, setPackingData] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [itemErrors, setItemErrors] = useState<Record<number, string>>({});
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [serialNumbers, setSerialNumbers] = useState<
    Record<number, SerialNumberOption[]>
  >({});
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] = useState(false);
  const [additionalDetailsContent, setAdditionalDetailsContent] = useState("");
  const [additionalDetailsIdx, setAdditionalDetailsIdx] = useState<number | null>(null);
  const [currentSerialNo, setCurrentSerialNo] = useState<string>("");
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [serialNumberOptions, setSerialNumberOptions] = useState<
    SerialNumberOption[]
  >([]);
  const [loadingSerialNumbers, setLoadingSerialNumbers] =
    useState<boolean>(false);
  const [shippingAddress, setShippingAddress] = useState<any>(null);
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [salesOrderProductId, setSalesOrderProductId] = useState<string>("");
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [selectedSerialOptions, setSelectedSerialOptions] = useState<
    SerialNumberOption[]
  >([]);

  // Get packing state from Redux store
  const selectPackingData = createSelector(
    (state: any) => state.WarehousePackingDone,
    (packing) => ({
      currentPacking: packing?.currentPacking || null,
      updateLoading: packing?.updateLoading || false,
      updateSuccess: packing?.updateSuccess || false,
      error: packing?.error || null,
      loading: packing?.loading || false,
    }),
  );

  const { currentPacking, loading, error, updateSuccess } = useSelector(
    (state: any) => state.PackingDone,
  );

  // Fetch packing data when component mounts
  useEffect(() => {
    if (id) {
      console.log("=== Fetching packing data for ID:", id, "===");
      dispatch(fetchWarehousePackingById(id));
    }
  }, [id, dispatch]);

  // Debug: Log currentPacking changes
  useEffect(() => {
    console.log("=== currentPacking changed ===", currentPacking);
  }, [currentPacking]);

  // Set default packing date
  const getDefaultDate = () => format(new Date(), "yyyy-MM-dd");

  const fetchSerialNumbers = async (
    productId: string,
    warehouseId?: string,
  ) => {
    if (!productId) {
      setSerialNumberOptions([]);
      return;
    }

    setLoadingSerialNumbers(true);
    try {
      const endpoint =
        API_ENDPOINT.WAREHOUSE.ITEM_INWARD.GRN.SERIAL_NO_DROPDOWN(
          productId,
          warehouseId,
        );
      const response = await WarehouseAxios.get(endpoint);

      const serialNumbersData = response?.data?.data || response?.data || [];
      const serialNumbersList: SerialNumberOption[] = Array.isArray(
        serialNumbersData,
      )
        ? serialNumbersData.map((item: any) => {
            if (typeof item === "string")
              return { serial_no: item, inventory_product_id: "" };
            return {
              serial_no:
                item?.serial_no || item?.serial_number || item?.value || "",
              inventory_product_id: item?.inventory_product_id || "",
            };
          })
        : [];

      setSerialNumberOptions(serialNumbersList);
    } catch (error: any) {
      console.error("Error fetching serial numbers:", error);
      showError("Failed to fetch serial numbers");
      setSerialNumberOptions([]);
    } finally {
      setLoadingSerialNumbers(false);
    }
  };

  const handleOpenModal = async (item: any, idx: number) => {
    setSelectedItem(item);
    setSelectedItemIndex(idx);

    const existingSerials = item?.serial_numbers || [];
    // console.log("existingSerials",existingSerials)
    setSerialNumbers((prev) => ({ ...prev, [idx]: existingSerials }));

    setSelectedSerialOptions([]); // clear temp selection

    await fetchSerialNumbers(item.product_id, warehouseId);
    setShowModal(true);
  };

  // const handleAddSerialNumber = () => {
  //   if (!currentSerialNo) {
  //     showWarning({ text: "Please select a serial number" });
  //     return;
  //   }

  //   if (selectedItemIndex === -1 || !selectedItem) return;

  //   const orderQty = selectedItem?.ordered_quantity || 0;
  //   const overallPackedQty = selectedItem?.overall_packed_qty || 0;
  //   const currentPackedQty = selectedItem?.current_packed_qty || 0;
  //   const balanceQty = selectedItem?.balance_qty || 0;
  //   const currentSerials = serialNumbers[selectedItemIndex] || [];

  //   // Packed from OTHER records (excluding this one)
  //   const packedFromOtherRecords = overallPackedQty - currentPackedQty;
  //   // Max allowed in this edit = balance + what was packed in this record
  //   const maxAllowed = balanceQty + currentPackedQty;

  //   if (currentSerials.length >= maxAllowed) {
  //     showWarning({
  //       text: `Maximum ${maxAllowed} serial numbers can be added for this item (Already packed in other records: ${packedFromOtherRecords})`
  //     });
  //     return;
  //   }

  //   // Check for duplicate serial numbers
  //   if (currentSerials.some(s => s.serial_no === currentSerialNo)) {
  //     showWarning({ text: "This serial number is already added" });
  //     return;
  //   }

  //   const selectedOption = serialNumberOptions.find(opt => opt.serial_no === currentSerialNo);

  //   if (selectedOption) {
  //     setSerialNumbers((prev) => ({
  //       ...prev,
  //       [selectedItemIndex]: [...currentSerials, selectedOption],
  //     }));
  //   }

  //   setCurrentSerialNo("");
  // };

  const handleAddSerialNumbers = () => {
    if (selectedItemIndex === -1 || selectedSerialOptions.length === 0) return;

    const currentSerials = serialNumbers[selectedItemIndex] || [];
    const maxAllowed = getMaxAllowedSerials();

    if (currentSerials.length + selectedSerialOptions.length > maxAllowed) {
      showWarning({
        text: `Cannot add more than ${maxAllowed} serial numbers`,
      });
      return;
    }

    setSerialNumbers((prev) => ({
      ...prev,
      [selectedItemIndex]: [...currentSerials, ...selectedSerialOptions],
    }));

    setSelectedSerialOptions([]); // reset dropdown
  };

  // const handleRemoveSerialNumber = (serialNo: string) => {
  //   if (selectedItemIndex === -1) return;

  //   const currentSerials = serialNumbers[selectedItemIndex] || [];
  //   const updatedSerials = currentSerials.filter((s) => s.serial_no !== serialNo);

  //   setSerialNumbers((prev) => ({
  //     ...prev,
  //     [selectedItemIndex]: updatedSerials,
  //   }));
  // };

  const handleRemoveSerialNumber = (serialNo: string) => {
    setSerialNumbers((prev) => ({
      ...prev,
      [selectedItemIndex]: prev[selectedItemIndex].filter(
        (s) => s.serial_no !== serialNo,
      ),
    }));
  };

  const handleSaveModal = () => {
    if (selectedItemIndex === -1) return;

    const currentSerials = serialNumbers[selectedItemIndex] || [];
    const orderQty = selectedItem?.ordered_quantity || 0;
    const overallPackedQty = selectedItem?.overall_packed_qty || 0;
    const currentPackedQty = selectedItem?.current_packed_qty || 0;
    const balanceQty = selectedItem?.balance_qty || 0;

    // Packed from OTHER records (excluding this one)
    const packedFromOtherRecords = overallPackedQty - currentPackedQty;
    // Max allowed in this edit = balance + what was packed in this record
    const maxAllowed = balanceQty + currentPackedQty;

    // Validation: Cannot exceed max allowed
    if (currentSerials.length > maxAllowed) {
      showWarning({
        text: `Cannot add more than ${maxAllowed} serial numbers (Already packed in other records: ${packedFromOtherRecords})`,
      });
      return;
    }

    // Update the item with serial numbers
    const updatedItems = [...items];
    updatedItems[selectedItemIndex].serial_numbers = currentSerials;
    // Note: Do NOT update current_packed_qty - it should remain as the original API value
    // The display calculations use serialNumbers[idx].length for the current count
    setItems(updatedItems);

    // Clear any previous errors for this item
    setItemErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[selectedItemIndex];
      return newErrors;
    });

    showSuccess("Serial numbers saved successfully");
    setShowModal(false);
    setCurrentSerialNo("");
  };

  const formik = useFormik<PackingFormValues>({
    initialValues: {
      packing_date: getDefaultDate(),
      initiated_by: "",
      so_number: "",
      notes: "",
      documents: null,
      tags: [],
    },
    validationSchema: Yup.object({
      packing_date: Yup.string().required("Packing Date is required"),
      initiated_by: Yup.string()
        .required("Initiated By is required")
        .min(2, "Initiated By must be at least 2 characters"),
      so_number: Yup.string().required("SO Number is required"),
      notes: Yup.string(),
      documents: Yup.mixed().nullable(),
    }),
    onSubmit: async (values) => {
      // Validation 1: At least one item is required
      if (!items || items.length === 0) {
        showWarning({ text: "At least one item is required for packing" });
        return;
      }

      // Validation 2: At least one item must have serial numbers (packed)
      const itemsWithSerialNumbers = items.filter(
        (item: any) =>
          item?.serial_numbers &&
          Array.isArray(item.serial_numbers) &&
          item.serial_numbers.length > 0,
      );

      if (itemsWithSerialNumbers.length === 0) {
        showWarning({
          text: "At least one item must have serial numbers added",
        });
        return;
      }

      // Validation 3: Check if any item exceeds allowed quantity
      // For edit: (overall_packed_qty - current_packed_qty) + new_serials <= ordered_quantity
      const hasExceededQuantity = items.some((item: any) => {
        const serialNums = item?.serial_numbers || [];
        const newPackedQty = serialNums.length;
        const overallPackedQty = item?.overall_packed_qty || 0;
        const currentPackedQty = item?.current_packed_qty || 0;
        const orderedQty = item?.ordered_quantity || 0;

        // Packed from other records + new packed quantity
        const packedFromOtherRecords = overallPackedQty - currentPackedQty;
        const totalPacked = packedFromOtherRecords + newPackedQty;
        return totalPacked > orderedQty;
      });

      if (hasExceededQuantity) {
        showWarning({
          text: "Total packed quantity cannot exceed ordered quantity for any item",
        });
        return;
      }

      // Validation 4: Check for any validation errors in itemErrors state
      if (Object.keys(itemErrors).length > 0) {
        showWarning({ text: "Please fix validation errors before submitting" });
        return;
      }

      // Create FormData for payload
      const formData = new FormData();

      // Add basic fields
      formData.append(
        "packing_date",
        new Date(values.packing_date).toISOString(),
      );
      formData.append("initiated_by", values.initiated_by);
      formData.append("sales_order_product_id", salesOrderProductId);
      formData.append("notes", values.notes || "");
      formData.append("warehouse_id", warehouseId);
      formData.append("packing_id", id || "");
      if (values.tags && values.tags.length) {
        formData.append("tags", JSON.stringify(values.tags));
      }

      // Add shipping address
      if (shippingAddress) {
        formData.append(
          "shipping_address_id",
          shippingAddress.shipping_address_id,
        );
        formData.append("shipping_address", JSON.stringify(shippingAddress));
      }

      // Add attachments
      // If user selected a new file, only send the new file
      // Otherwise, send existing attachments that weren't removed
      if (values.documents) {
        // New file selected - only send new file
        formData.append("attachments", values.documents);
      } else if (existingAttachments.length > 0) {
        // No new file selected - send existing attachments
        formData.append(
          "existing_attachments",
          JSON.stringify(existingAttachments),
        );
      }

      // Build packing products array - ALWAYS send all products with serial numbers
      const packingProducts: any[] = [];

      itemsWithSerialNumbers.forEach((item: any) => {
        const serials = item.serial_numbers || [];

        serials.forEach((serialItem: SerialNumberOption) => {
          packingProducts.push({
            sales_order_product_item_id: item?.sales_order_product_item_id,
            product_id: item?.product_id,
            inventory_product_id: serialItem.inventory_product_id,
            serial_number: serialItem.serial_no,
            product_snapshot: {
              item_name: item?.product_name,
              product_code: item?.product_code,
              category: item?.category,
              subcategory: item?.subcategory,
              make: item?.make,
              model: item?.model,
              unit_name: item?.unit_name,
              product_sku: item?.product_sku,
              hsn_code: item?.hsn_code,
              shipping_address: shippingAddress,
            },
            additional_details: item.additional_details || "",
          });
        });
      });

      formData.append("packing_products", JSON.stringify(packingProducts));

      console.log("Update Payload:");
      console.log("packing_id:", id);
      console.log("packing_date:", values.packing_date);
      console.log("initiated_by:", values.initiated_by);
      console.log("sales_order_product_id:", salesOrderProductId);
      console.log("warehouse_id:", warehouseId);
      console.log("shipping_address_id:", shippingAddress?.shipping_address_id);
      console.log("notes:", values.notes);
      console.log("packing_products:", packingProducts);

      // Dispatch the update packing thunk
      try {
        await dispatch(updateWarehousePacking({ id, formData })).unwrap();
        showSuccess("Packing updated successfully").then(() => {
          navigate(-1);
        });
      } catch (error) {
        console.error("Error updating packing:", error);
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount);

  // Handle new file selection - clear old attachments when new file is selected
  useEffect(() => {
    if (formik.values.documents) {
      // When user selects a new file, remove all existing attachments
      setExistingAttachments([]);
    }
  }, [formik.values.documents]);

  // Process packing details from API response
  useEffect(() => {
    console.log("=== EditPacking: currentPacking received ===", currentPacking);

    // Handle different possible Redux store structures
    let data = null;

    if (currentPacking?.data) {
      // Structure 1: { data: {...} }
      data = currentPacking.data;
    } else if (
      currentPacking &&
      typeof currentPacking === "object" &&
      currentPacking.packing_id
    ) {
      // Structure 2: Direct packing object
      data = currentPacking;
    }

    if (!data) {
      console.warn("=== No valid packing data found ===");
      return;
    }

    console.log("=== EditPacking: Processing data ===", data);

    // Set basic details
    setPackingData(data);
    setWarehouseId(data.warehouse_id || "");
    setSalesOrderProductId(data.sales_order_product_id || "");

    // Set shipping address - parse if it's a JSON string
    if (data.shipping_address) {
      try {
        const shippingAddr =
          typeof data.shipping_address === "string"
            ? JSON.parse(data.shipping_address)
            : data.shipping_address;
        console.log("=== Parsed shipping address ===", shippingAddr);
        setShippingAddress(shippingAddr);
      } catch (error) {
        console.error("Error parsing shipping address:", error);
        setShippingAddress(null);
      }
    }

    // Load existing attachments from API response
    if (data.attachments && Array.isArray(data.attachments)) {
      console.log("=== Setting existing attachments ===", data.attachments);
      setExistingAttachments(data.attachments);
    } else {
      setExistingAttachments([]);
    }

    // Set form values
    const soNumber = data.sales_order_product?.sales_order_number || "";
    const packingDateFormatted = data.packing_date
      ? format(new Date(data.packing_date), "yyyy-MM-dd")
      : getDefaultDate();
    const initiatedBy = data.initiated_by || "";
    const notes = data.notes || "";
    const tags = data.tags || [];

    console.log("=== Setting form values ===", {
      soNumber,
      packingDateFormatted,
      initiatedBy,
      notes,
    });

    formik.setFieldValue("so_number", soNumber);
    formik.setFieldValue("packing_date", packingDateFormatted);
    formik.setFieldValue("initiated_by", initiatedBy);
    formik.setFieldValue("notes", notes);
    formik.setFieldValue("tags", tags);
    if (soNumber)
      setTimeout(() => formik.setFieldError("so_number", undefined), 0);
    if (packingDateFormatted)
      setTimeout(() => formik.setFieldError("packing_date", undefined), 0);
    if (initiatedBy && initiatedBy.length >= 2)
      setTimeout(() => formik.setFieldError("initiated_by", undefined), 0);

    // Process products
    if (data.products && Array.isArray(data.products)) {
      console.log(
        "=== Processing products ===",
        data.products.length,
        "products found",
      );

      const processedItems = data.products.map((product: any, idx: number) => {
        // Parse serial numbers from API response
        const serialNumsRaw = product?.serial_numbers || [];
        const serialNums: SerialNumberOption[] = Array.isArray(serialNumsRaw)
          ? serialNumsRaw.map((s: any) =>
              typeof s === "string"
                ? { serial_no: s, inventory_product_id: "" }
                : {
                    serial_no: s?.serial_no || s?.serial_number || "",
                    inventory_product_id: s?.inventory_product_id || "",
                  },
            )
          : [];

        return {
          sales_order_product_item_id: product?.sales_order_product_item_id,
          product_id: product?.product_id,
          product_name:
            product?.product_name ||
            product?.product_sku ||
            product?.product_code ||
            "N/A",
          product_code: product?.product_code || "N/A",
          product_sku: product?.product_sku || "N/A",
          unit_name: product?.unit_name || "N/A",
          category: product?.category_name || product?.category || "N/A",
          subcategory:
            product?.sub_category_name || product?.subcategory || "N/A",
          make: product?.make_name || product?.make || "N/A",
          model: product?.model_name || product?.model || "N/A",
          hsn_code: product?.product_hsn || product?.hsn_code || "N/A",
          ordered_quantity: product?.ordered_quantity || 0,
          overall_packed_qty: product?.overall_packed_qty || 0, // Total packed for this product across all packings
          current_packed_qty: product?.current_packed_qty || 0, // Quantity packed in THIS packing record
          balance_qty: product?.balance_qty || 0, // Balance quantity remaining to be packed
          serial_numbers: serialNums,
          additional_details: product?.additional_details || "",
          in_stock: product?.in_stock || 0,
        };
      });

      console.log("=== Processed items ===", processedItems);
      setItems(processedItems);

      // Initialize serial numbers state with existing serials
      const serialsState: Record<number, SerialNumberOption[]> = {};
      processedItems.forEach((item: any, idx: number) => {
        if (item?.serial_numbers && item.serial_numbers.length > 0) {
          serialsState[idx] = item.serial_numbers;
        }
      });

      console.log("=== Serial numbers state ===", serialsState);

      if (Object.keys(serialsState).length > 0) {
        setSerialNumbers(serialsState);
      }
    } else {
      console.warn("=== No products array found in data ===");
    }
    // End of data processing
  }, [currentPacking]);

  // Handle update success
  //   useEffect(() => {
  //     if (updateSuccess) {
  //       showSuccess("Packing updated successfully").then(() => {
  //         navigate("/warehouse-packing-done");
  //       });
  //     }
  //   }, [updateSuccess, navigate]);

  // Handle errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || "Failed to update packing";
      showError(errorMessage);
    }
  }, [error]);

  const formatAddress = (address: any) => {
    if (!address) return "";
    return `${address.address}, ${address.city}, ${address.state}, ${
      address.country
    } - ${address.pin_code || address.pincode}`;
  };

  const getMaxAllowedSerials = () => {
    if (!selectedItem) return 0;

    const balanceQty = selectedItem.balance_qty || 0;
    const currentPackedQty = selectedItem.current_packed_qty || 0;

    return balanceQty + currentPackedQty;
  };

  const getAvailableSerialNumbers = () => {
    const existingSerials = serialNumbers[selectedItemIndex] || [];

    return serialNumberOptions.filter(
      (opt) => !existingSerials.some((s) => s.serial_no === opt.serial_no),
    );
  };

  // Helper to format the address
  const formattedAddress = shippingAddress
    ? formatAddress(shippingAddress)
    : "";

  return (
    <>
      <div
        className="page-content px-4"
        style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
      >
        <Container
          fluid
          style={{
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 0 15px rgba(0,0,0,0.1)",
          }}
        >
          <FormBreadcrumb
            formName="Packing"
            isEdit={true}
            navigatePath="/warehouse-packing"
          />

          <hr />
          {pageLoading || loading || loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "60vh" }}
            >
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <Form autoComplete="off" onSubmit={formik.handleSubmit}>
                {/* Packing Details Section */}
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Packing Details</h5>
                  <Row
                    style={{ backgroundColor: MecordColors.LightGrayColor }}
                    className="p-2 border-radius-xl"
                  >
                    <Col lg={3}>
                      <div className="mb-3">
                        <Form.Label>Packing Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="packing_date"
                          value={formik.values.packing_date}
                          onChange={(e) => {
                            formik.handleChange(e);
                            if ((e.target.value || "").trim())
                              setTimeout(
                                () =>
                                  formik.setFieldError(
                                    "packing_date",
                                    undefined,
                                  ),
                                0,
                              );
                          }}
                          onBlur={formik.handleBlur}
                          isInvalid={
                            !!(
                              formik.errors.packing_date &&
                              formik.touched.packing_date
                            )
                          }
                        />
                        {formik.errors.packing_date &&
                          formik.touched.packing_date && (
                            <Form.Control.Feedback type="invalid">
                              {formik.errors.packing_date}
                            </Form.Control.Feedback>
                          )}
                      </div>
                    </Col>
                    <Col lg={3}>
                      <div className="mb-3">
                        <Form.Label>
                          Initiated By <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="initiated_by"
                          placeholder="Enter Name"
                          value={formik.values.initiated_by}
                          onChange={(e) => {
                            formik.handleChange(e);
                            const v = (e.target.value || "").trim();
                            if (v.length >= 2)
                              setTimeout(
                                () =>
                                  formik.setFieldError(
                                    "initiated_by",
                                    undefined,
                                  ),
                                0,
                              );
                          }}
                          onBlur={formik.handleBlur}
                          isInvalid={
                            !!formik.errors.initiated_by &&
                            formik.touched.initiated_by
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.initiated_by}
                        </Form.Control.Feedback>
                      </div>
                    </Col>
                    <Col lg={3}>
                      <div className="mb-3">
                        <Form.Label>SO Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="so_number"
                          value={formik.values.so_number}
                          readOnly
                          disabled
                          style={{
                            backgroundColor: "#e9ecef",
                            cursor: "not-allowed",
                          }}
                        />
                      </div>
                    </Col>
                    <Col lg={3}>
                      <div className="mb-3">
                        <Form.Label>Shipping Address</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{formattedAddress}</Tooltip>}
                        >
                          <Form.Control
                            type="text"
                            value={formattedAddress}
                            readOnly
                            disabled
                            style={{
                              backgroundColor: "#e9ecef",
                              cursor: "not-allowed",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          />
                        </OverlayTrigger>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Tags Section */}
                <Row>
                  <Col lg={6} className="mb-4">
                    <AddTags
                      label="Add Tags"
                      placeholder="Enter tags"
                      value={formik.values.tags || []}
                      onChange={(tags) => formik.setFieldValue("tags", tags)}
                    />
                  </Col>
                </Row>

                {/* Product Details Section */}
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Product Details</h5>
                  <div style={{ overflowX: "auto" }}>
                    <table className="table">
                      <thead
                        style={{ backgroundColor: MecordColors.LightGrayColor }}
                      >
                        <tr>
                          <th style={{ minWidth: "80px" }}>S.No</th>
                          <th style={{ minWidth: "200px" }}>Products</th>
                          <th style={{ minWidth: "120px" }}>Product Code</th>
                          <th style={{ minWidth: "120px" }}>SKU</th>
                          <th style={{ minWidth: "120px" }}>Order Quantity</th>
                          <th style={{ minWidth: "150px" }}>Packed Quantity</th>
                          <th style={{ minWidth: "150px" }}>
                            Balance Quantity
                          </th>
                          <th style={{ minWidth: "150px" }}>Additional Details</th>
                          <th
                            style={{
                              minWidth: "100px",
                              position: "sticky",
                              right: 0,
                              background: MecordColors.LightGrayColor,
                              zIndex: 2,
                            }}
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="align-middle">
                        {items.map((item: any, idx: number) => {
                          const currentSerials = serialNumbers[idx] || [];
                          const newSerialCount = currentSerials.length;
                          const orderedQty = item?.ordered_quantity || 0;
                          const overallPackedQty =
                            item?.overall_packed_qty || 0;
                          const currentPackedQty =
                            item?.current_packed_qty || 0;
                          const balanceQty = item?.balance_qty || 0;

                          // Packed from OTHER records (excluding this one being edited)
                          const packedFromOtherRecords =
                            overallPackedQty - currentPackedQty;
                          // New display values
                          const newPackedQty =
                            packedFromOtherRecords + newSerialCount;
                          const newBalanceQty =
                            balanceQty + currentPackedQty - newSerialCount;

                          return (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>
                                <div>
                                  <div className="fw-semibold">
                                    {item?.product_name || "-"}
                                  </div>
                                  {item?.hsn_code && (
                                    <div
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#6c757d",
                                      }}
                                    >
                                      HSN: {item.hsn_code}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>{item?.product_code || "-"}</td>
                              <td>{item?.product_sku || "-"}</td>
                              <td>{orderedQty}</td>
                              <td>
                                <div>
                                  {newPackedQty}
                                  {newPackedQty > orderedQty && (
                                    <div
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#dc3545",
                                        marginTop: "2px",
                                      }}
                                    >
                                      (Exceeds order qty!)
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                {newBalanceQty >= 0 ? newBalanceQty : 0}
                              </td>
                              <td>
                                <div className="text-center">
                                  <EditIcon
                                    style={{
                                      cursor: "pointer",
                                      color: MecordColors.ThemeBGColor,
                                      fontSize: "20px",
                                    }}
                                    onClick={() => {
                                      setAdditionalDetailsIdx(idx);
                                      setAdditionalDetailsContent(
                                        item.additional_details || "",
                                      );
                                      setShowAdditionalDetailsModal(true);
                                    }}
                                  />
                                </div>
                              </td>
                              <td
                                style={{
                                  minWidth: "100px",
                                  position: "sticky",
                                  right: 0,
                                  background: "white",
                                  zIndex: 2,
                                }}
                              >
                                <Button
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor:
                                      MecordColors.PrimaryButtonBGColor,
                                    color: MecordColors.PrimaryButtonTextColor,
                                    border: "none",
                                  }}
                                  onClick={() => handleOpenModal(item, idx)}
                                >
                                  <i className="bi bi-pencil me-1"></i>
                                  Edit
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="mb-4">
                  <div
                    className="d-flex justify-content-between align-items-center p-2 rounded"
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                    }}
                    onClick={() => setIsNotesOpen(!isNotesOpen)}
                  >
                    <h6
                      className="fw-semibold mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {isNotesOpen ? "▼" : "▶"} Notes
                    </h6>
                  </div>
                  {isNotesOpen && (
                    <div className="p-2">
                      <Form.Group>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="notes"
                          placeholder="Enter notes"
                          value={formik.values.notes}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Form.Group>
                    </div>
                  )}
                </div>

                {/* Attach Files Section */}
                <div className="mb-4">
                  <div className="mb-3">
                    <h6 className="fw-semibold mb-1">Attachment</h6>

                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: "0 0 auto" }}>
                        <FileUploadInput
                          label=""
                          name="documents"
                          formik={formik}
                        />
                      </div>
                      {/* {formik.values.documents && (
                        <small className="text-success">
                          ✓ {formik.values.documents.name}
                        </small>
                      )} */}
                    </div>

                    {/* Display existing attachments */}
                    {existingAttachments.length > 0 && (
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        {existingAttachments.map((attachment, idx) => {
                          const fileName =
                            attachment.split("/").pop() || "Attachment";
                          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
                            fileName,
                          );
                          // const attachmentUrl = attachment.startsWith('http') ? attachment : `${WarehouseAxios.defaults.baseURL}/${attachment}`;

                          return (
                            <div
                              key={idx}
                              className="d-flex align-items-center gap-1"
                            >
                              {isImage ? (
                                <img
                                  src={attachment}
                                  alt={fileName}
                                  style={{
                                    // maxWidth: '150px',
                                    maxHeight: "150px",
                                    borderRadius: "4px",
                                    border: "1px solid #ddd",
                                  }}
                                />
                              ) : (
                                <a
                                  href={attachment}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-none"
                                  title={fileName}
                                >
                                  📄{" "}
                                  {fileName.length > 20
                                    ? fileName.substring(0, 20) + "..."
                                    : fileName}
                                </a>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-danger p-0"
                                style={{ minWidth: "24px", height: "24px" }}
                                onClick={() => {
                                  setExistingAttachments((prev) =>
                                    prev.filter((_, i) => i !== idx),
                                  );
                                }}
                                title="Remove attachment"
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-4">
                  <CancelButton onClick={() => navigate(-1)} />
                  <PrimarySubmitButton label="Update" />
                </div>
              </Form>
            </>
          )}
        </Container>
      </div>

      {/* Serial Numbers Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          // Revert serial numbers to what's in the item (discard unsaved changes)
          if (selectedItemIndex !== -1 && selectedItem) {
            const existingSerials = selectedItem?.serial_numbers || [];
            setSerialNumbers((prev) => ({
              ...prev,
              [selectedItemIndex]: existingSerials,
            }));
          }
          setShowModal(false);
          setCurrentSerialNo("");
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage Serial Numbers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <>
              {/* Product Details Table */}
              <div className="mb-4">
                <table className="table table-bordered">
                  <thead style={{ backgroundColor: "#E4E4E4" }}>
                    <tr>
                      <th>Products</th>
                      <th>Product Code</th>
                      <th>In Stock</th>
                      <th>Order Quantity</th>
                      <th>Packed Quantity</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <div className="fw-semibold">
                            {selectedItem?.product_name || "-"}
                          </div>
                          {selectedItem?.product_sku && (
                            <div
                              style={{ fontSize: "0.75rem", color: "#6c757d" }}
                            >
                              SKU: {selectedItem.product_sku}
                            </div>
                          )}
                          {selectedItem?.hsn_code && (
                            <div
                              style={{ fontSize: "0.75rem", color: "#6c757d" }}
                            >
                              HSN: {selectedItem.hsn_code}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{selectedItem?.product_code || "-"}</td>
                      <td>{getAvailableSerialNumbers()?.length ?? 0}</td>

                      <td>{selectedItem?.ordered_quantity || 0}</td>
                      <td>
                        <div>
                          {(() => {
                            const overallPackedQty =
                              selectedItem?.overall_packed_qty || 0;
                            const currentPackedQty =
                              selectedItem?.current_packed_qty || 0;
                            const currentSerials =
                              serialNumbers[selectedItemIndex] || [];
                            const packedFromOtherRecords =
                              overallPackedQty - currentPackedQty;
                            return (
                              packedFromOtherRecords + currentSerials.length
                            );
                          })()}
                        </div>
                      </td>
                      <td>
                        <div>
                          {(() => {
                            const balanceQty = selectedItem?.balance_qty || 0;
                            const currentPackedQty =
                              selectedItem?.current_packed_qty || 0;
                            const currentSerials =
                              serialNumbers[selectedItemIndex] || [];
                            const newBalance =
                              balanceQty +
                              currentPackedQty -
                              currentSerials.length;
                            return newBalance >= 0 ? newBalance : 0;
                          })()}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Serial Number Input Section */}
              <div className="mb-4">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Serial No.</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        {loadingSerialNumbers ? (
                          <div className="d-flex align-items-center">
                            <Spinner size="sm" className="me-2" />
                            <span>Loading serial numbers...</span>
                          </div>
                        ) : (
                          // <Form.Select
                          //   value={currentSerialNo}
                          //   onChange={(e) => setCurrentSerialNo(e.target.value)}
                          //   disabled={
                          //     (() => {
                          //       const balanceQty = selectedItem?.balance_qty || 0;
                          //       const currentPackedQty = selectedItem?.current_packed_qty || 0;
                          //       const maxAllowed = balanceQty + currentPackedQty;
                          //       const currentSerials = serialNumbers[selectedItemIndex] || [];
                          //       return currentSerials.length >= maxAllowed || serialNumberOptions.length === 0;
                          //     })()
                          //   }
                          // >
                          //   <option value="">Select Serial No.</option>
                          //   {serialNumberOptions
                          //     .filter((serialNo) => {
                          //       const currentSerials = serialNumbers[selectedItemIndex] || [];
                          //       return !currentSerials.some(s => s.serial_no === serialNo.serial_no);
                          //     })
                          //     .map((serialNo, idx) => (
                          //       <option key={idx} value={serialNo.serial_no}>
                          //         {serialNo.serial_no}
                          //       </option>
                          //     ))}
                          // </Form.Select>
                          <Autocomplete
                            multiple
                            size="small"
                            disableCloseOnSelect
                            loading={loadingSerialNumbers}
                            /* ✅ Available serials (removed ones reappear automatically) */
                            options={getAvailableSerialNumbers()}
                            value={selectedSerialOptions}
                            getOptionLabel={(option) => option.serial_no}
                            isOptionEqualToValue={(option, value) =>
                              option.serial_no === value.serial_no
                            }
                            /* ✅ Balance-aware validation (edit mode safe) */
                            onChange={(_, value) => {
                              const maxAllowed = getMaxAllowedSerials();
                              const alreadyAdded = (
                                serialNumbers[selectedItemIndex] || []
                              ).length;

                              if (value.length + alreadyAdded > maxAllowed) {
                                showWarning({
                                  text: `Cannot select more than ${maxAllowed} serial numbers`,
                                });
                                return;
                              }

                              setSelectedSerialOptions(value);
                            }}
                            /* ✅ Mecord styled dropdown option */
                            renderOption={(props, option, { selected }) => (
                              <li {...props} style={{ padding: 0 }}>
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 0.8,
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: "0.75rem",

                                    // Hover state
                                    "&:hover": {
                                      backgroundColor: MecordColors.Hover,
                                      color: MecordColors.ActiveText,
                                    },

                                    // Selected state
                                    ...(selected && {
                                      backgroundColor:
                                        MecordColors.ActiveBackground,
                                      color: MecordColors.ActiveText,
                                    }),
                                  }}
                                >
                                  <span>{option.serial_no}</span>

                                  {/* ❌ Remove from temporary selection */}
                                  {selected && (
                                    <span
                                      style={{
                                        cursor: "pointer",
                                        marginLeft: "10px",
                                        color: MecordColors.ThemeBGColor,
                                        fontWeight: "bold",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSerialOptions((prev) =>
                                          prev.filter(
                                            (s) =>
                                              s.serial_no !== option.serial_no,
                                          ),
                                        );
                                      }}
                                    >
                                      ✕
                                    </span>
                                  )}
                                </Box>
                              </li>
                            )}
                            ListboxProps={
                              autocompleteDropdownStyles.ListboxProps
                            }
                            renderInput={(params) => (
                              <BootstrapTextField
                                {...params}
                                placeholder="Select Serial Numbers"
                                size="small"
                                sx={{
                                  ...autocompleteDropdownStyles.inputSx,
                                }}
                              />
                            )}
                            /* ✅ Mecord styled selected tags */
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                  index,
                                });
                                return (
                                  <Box
                                    key={option.serial_no}
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      backgroundColor:
                                        MecordColors.ActiveBackground,
                                      borderRadius: "3px",
                                      padding: "2px 6px",
                                      margin: "1px",
                                      fontFamily: "Poppins, sans-serif",
                                      fontSize: "0.75rem",
                                    }}
                                    {...tagProps}
                                  >
                                    {option.serial_no}
                                  </Box>
                                );
                              })
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "#ced4da",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#ced4da",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#ced4da",
                                },
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Button
                          onClick={handleAddSerialNumbers}
                          disabled={selectedSerialOptions.length === 0}
                          style={{
                            backgroundColor: MecordColors.PrimaryButtonBGColor, // or MecordColors.primary
                            color: MecordColors.PrimaryButtonTextColor,
                            border: MecordColors.PrimaryButtonBGColor,
                          }}
                        >
                          Add
                        </Button>{" "}
                      </td>
                    </tr>
                  </tbody>
                </table>
                {(() => {
                  const balanceQty = selectedItem?.balance_qty || 0;
                  const currentPackedQty =
                    selectedItem?.current_packed_qty || 0;
                  const overallPackedQty =
                    selectedItem?.overall_packed_qty || 0;
                  const maxAllowed = balanceQty + currentPackedQty;
                  const packedFromOtherRecords =
                    overallPackedQty - currentPackedQty;
                  const currentSerials = serialNumbers[selectedItemIndex] || [];

                  if (currentSerials.length >= maxAllowed) {
                    return (
                      <div
                        className="text-warning"
                        style={{ fontSize: "0.875rem" }}
                      >
                        Maximum {maxAllowed} serial numbers can be added
                        (Already packed in other records:{" "}
                        {packedFromOtherRecords})
                      </div>
                    );
                  }
                  return null;
                })()}
                {serialNumberOptions.length === 0 && !loadingSerialNumbers && (
                  <div
                    className="text-warning"
                    style={{ fontSize: "0.875rem" }}
                  >
                    No serial numbers available for this product
                  </div>
                )}
              </div>

              {/* Added Serial Numbers List */}
              {(serialNumbers[selectedItemIndex] || []).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-semibold mb-2">
                    Serial Numbers (
                    {(serialNumbers[selectedItemIndex] || []).length}):
                  </h6>
                  <div
                    className="border rounded p-2"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    <div className="d-flex flex-wrap gap-2">
                      {(serialNumbers[selectedItemIndex] || []).map(
                        (serialItem, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center gap-2 border rounded px-2 py-1"
                            style={{ backgroundColor: "#f8f9fa" }}
                          >
                            <span>{serialItem.serial_no}</span>
                            <button
                              type="button"
                              className="btn-close btn-close-sm"
                              onClick={() => {
                                // 1️⃣ Remove from added serials
                                handleRemoveSerialNumber(serialItem.serial_no);

                                // 2️⃣ Add back to dropdown options with ID
                                setSerialNumberOptions((prevOptions) => {
                                  const exists = prevOptions.some(
                                    (opt) =>
                                      opt.serial_no === serialItem.serial_no,
                                  );
                                  if (exists) return prevOptions;

                                  return [
                                    {
                                      serial_no: serialItem.serial_no,
                                      inventory_product_id:
                                        serialItem.inventory_product_id, // add ID here
                                    },
                                    ...prevOptions,
                                  ];
                                });

                                // 3️⃣ Remove from selected options (Autocomplete)
                                setSelectedSerialOptions((prev) =>
                                  prev.filter(
                                    (s) => s.serial_no !== serialItem.serial_no,
                                  ),
                                );
                              }}
                              aria-label="Remove"
                              style={{ fontSize: "0.7rem" }}
                            ></button>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <CancelButton
            onClick={() => {
              if (selectedItemIndex !== -1 && selectedItem) {
                const existingSerials = selectedItem?.serial_numbers || [];
                setSerialNumbers((prev) => ({
                  ...prev,
                  [selectedItemIndex]: existingSerials,
                }));
              }
              setShowModal(false);
              setCurrentSerialNo("");
            }}
            showConfirmation={false}
          />
          <PrimarySubmitButton onClick={handleSaveModal} />
        </Modal.Footer>
      </Modal>

      {/* Additional Details Modal */}
      <Modal
        show={showAdditionalDetailsModal}
        onHide={() => setShowAdditionalDetailsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Additional Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: "350px" }}>
          <Form.Group className="mb-0">
            <Form.Label>Details</Form.Label>
            <TextEditor
              value={additionalDetailsContent}
              onChange={setAdditionalDetailsContent}
              placeholder="Enter additional details here..."
              minHeightPx={250}
              maxHeightPx={400}
              blockImages={true}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <CancelButton
            onClick={() => setShowAdditionalDetailsModal(false)}
            showConfirmation={false}
          />
          <Button
            style={{
              backgroundColor: MecordColors.PrimaryButtonBGColor,
              color: MecordColors.PrimaryButtonTextColor,
              border: "none",
            }}
            onClick={() => {
              if (additionalDetailsIdx !== null) {
                const newItems = [...items];
                newItems[additionalDetailsIdx].additional_details =
                  additionalDetailsContent;
                setItems(newItems);
              }
              setShowAdditionalDetailsModal(false);
            }}
          >
            Save Details
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditPacking;
